/**
 * NumberStepper — nudge a value up or down, with no text entry.
 *
 * Replaces the my $ / my pts inputs. Typed fields brought a focus-loss risk to
 * every render (a remount mid-keystroke drops the caret) and cost more width
 * than they earned: the real use is nudging, because you mostly agree with the
 * board and want a player a few dollars up or down.
 *
 * 🔴 BUT NUDGING ALONE WOULD BE WORSE THAN WHAT IT REPLACES. 18.5 → 25.0 PPG is
 * 65 presses; $1 → $62 is 61. So the big move has two accelerators, both
 * standard:
 *
 *   • press and hold repeats, accelerating after ~500ms — a 65-step move is a
 *     hold of about a second and a half
 *   • shift-click steps by 10× — $10, or 1.0 PPG
 *
 * ArrowUp/ArrowDown work when a stepper has focus, because that is how a
 * stepper is expected to behave and it costs nothing.
 *
 * 🔴 FLOAT DRIFT IS HANDLED BY ROUNDING ON EVERY WRITE, not by storing tenths.
 * Repeated `+= 0.1` reaches 18.599999999999998 and then the whole column reads
 * as noise. Rounding at the write keeps the sheet in natural units (21, 18.6)
 * rather than making every reader divide by ten, and `normalise` below is the
 * single place it happens. numberStepper.test.js walks 30 steps up and 30 back
 * down and demands the exact original value.
 *
 * 🔴 AND THERE IS A WAY BACK. A text field's empty state used to mean "no
 * override, follow the engine". A stepper has no empty state, so the first
 * nudge would otherwise create an override with no way to remove it short of
 * clearing the whole league. The × restores NO OVERRIDE — not the engine's
 * current number written as an override, which is a different state: an
 * override that happens to equal today's projection will not follow that
 * projection when it changes.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { theme } from "../../data/theme";

const HOLD_DELAY_MS = 500;   // before a held press starts repeating
const HOLD_MIN_MS = 45;      // fastest repeat
const HOLD_DECAY = 0.75;     // how quickly it gets there
// Exported because the target card steps by keyboard through its own handler
// and has to agree with these buttons exactly — see PpgField in TargetCell.
export const SHIFT_MULTIPLIER = 10;

/** One place where rounding happens, so drift cannot appear anywhere else. */
export function normalise(value, decimals) {
  const n = Number(value) || 0;
  const floored = Math.max(0, n);          // price floor 0, PPG floor 0
  return decimals > 0
    ? Math.round(floored * 10 ** decimals) / 10 ** decimals
    : Math.round(floored);
}

function Arrow({ dir, onStart, onStop, disabled }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={dir > 0 ? "increase" : "decrease"}
      disabled={disabled}
      onPointerDown={(e) => { e.preventDefault(); onStart(dir, e.shiftKey); }}
      onPointerUp={onStop}
      onPointerLeave={onStop}
      onPointerCancel={onStop}
      style={{
        width: 12, height: 9, padding: 0, lineHeight: "7px",
        border: "none", background: "transparent",
        color: theme.colors.text.faint,
        fontSize: 9, cursor: disabled ? "default" : "pointer",
        display: "block",
      }}
    >{dir > 0 ? "▲" : "▼"}</button>
  );
}

export default function NumberStepper({
  value,          // the override, or undefined when following `base`
  base,           // the engine's / board's number
  step = 1,
  decimals = 0,
  prefix = "",
  onChange,
  onReset,
  title,
  width = 30,
}) {
  const overridden = value !== undefined;
  const effective = overridden ? value : base;

  // 🔴 The hold accumulates in a ref rather than re-reading the prop each tick.
  // A repeating timer would otherwise read a `value` captured before the parent
  // re-rendered, and every tick after the first would step from the same stale
  // number — a 60-step hold landing one step from where it started.
  const accRef = useRef(effective);
  const timerRef = useRef(null);

  const stop = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => stop, [stop]);   // never leave a timer running past unmount

  const bump = useCallback((dir, shift) => {
    const delta = dir * step * (shift ? SHIFT_MULTIPLIER : 1);
    accRef.current = normalise(accRef.current + delta, decimals);
    if (onChange) onChange(accRef.current);
  }, [step, decimals, onChange]);

  const start = useCallback((dir, shift) => {
    stop();
    accRef.current = normalise(effective, decimals);
    bump(dir, shift);
    let delay = HOLD_DELAY_MS;
    const tick = () => {
      bump(dir, shift);
      delay = Math.max(HOLD_MIN_MS, delay * HOLD_DECAY);
      timerRef.current = setTimeout(tick, delay);
    };
    timerRef.current = setTimeout(tick, delay);
  }, [bump, effective, decimals, stop]);

  const onKeyDown = (e) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    accRef.current = normalise(effective, decimals);
    bump(e.key === "ArrowUp" ? 1 : -1, e.shiftKey);
  };

  const shown = decimals > 0 ? effective.toFixed(decimals) : String(Math.round(effective));

  return (
    <span
      tabIndex={0}
      onKeyDown={onKeyDown}
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 1,
        outline: "none", flexShrink: 0,
      }}
    >
      <span
        // Whose number this is, on hover — and the handle the tests use rather
        // than reaching into the DOM for a child node.
        title={overridden ? "Your number" : "Mock1's number"}
        style={{
        width, textAlign: "right",
        fontFamily: theme.fonts.display,
        fontSize: theme.fontSize.small,
        fontWeight: theme.fontWeight.bold,
        // Muted while it is just the base; your ink once you have touched it,
        // so a glance down the column says which rows you have an opinion on.
        color: overridden ? theme.colors.brand.orange : theme.colors.text.faint,
        }}>{prefix}{shown}</span>

      <span style={{ display: "flex", flexDirection: "column" }}>
        <Arrow dir={1} onStart={start} onStop={stop} />
        <Arrow dir={-1} onStart={start} onStop={stop} />
      </span>

      {/* Reserved whether or not it is shown, so the column cannot go ragged. */}
      <span style={{ width: 10, display: "inline-flex", justifyContent: "center" }}>
        {overridden && (
          <button
            type="button"
            aria-label="clear override"
            title="Back to the projection — removes your override rather than freezing today's number"
            onClick={() => onReset && onReset()}
            style={{
              width: 10, height: 12, padding: 0, lineHeight: "10px",
              border: "none", background: "transparent",
              color: theme.colors.text.faint,
              fontSize: 10, cursor: "pointer",
            }}
          >&#10005;</button>
        )}
      </span>
    </span>
  );
}
